import { useState, useEffect } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import AuthLinkTable from './AuthLinkTable';
import CreateClientModal from '../../Modals/CreateClientModal';
import AuthLinkModal from './AuthLinkModal';
import XpektorButton from '../../../../Components/XpektorButton/XpektorButton';
import AuthService from '../../../../Services/AuthService';

const gT = (t, key) => t(`page-admin-settings.auth-link-settings${key}`);
const gsT = (t, key) => t(`shared${key}`);

/* eslint-disable react/prop-types */
export default function AuthLinkSettings({ t }) {
  const [authLinks, setAuthLinks] = useState([]);
  const [selectedAuthLink, setSelectedAuthLink] = useState();
  const [showAuthLinkModal, setShowAuthLinkModal] = useState(false);
  const [showCreateAuthLinkModal, setShowCreateAuthLinkModal] = useState(false);

  const fetchExternalStates = async () => {
    await AuthService.getAllExternalStates()
      .then((response) => {
        setAuthLinks(response.data);
      })
      .catch(() => {
        setAuthLinks([]);
      });
  };

  const [order, setOrder] = useState('asc');

  const sortStringCompare = (a, b) => {
    const stringA = a?.toUpperCase() ?? '';
    const stringB = b?.toUpperCase() ?? '';

    if (stringA < stringB) {
      return -1;
    }
    if (stringA > stringB) {
      return 1;
    }
    return 0;
  };

  const sortByName = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return sortStringCompare(a.targetClient, b.targetClient);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return sortStringCompare(a.targetClient, b.targetClient) * -1;
      });
      setOrder('asc');
    }
  };

  const sortByUserName = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return sortStringCompare(a.requestingUser, b.requestingUser);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return sortStringCompare(a.requestingUser, b.requestingUser) * -1;
      });
      setOrder('asc');
    }
  };

  const dateCompare = (a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);

    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  };
  const sortByDateCreated = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return dateCompare(a.dateCreated, b.dateCreated);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return dateCompare(a.dateCreated, b.dateCreated) * -1;
      });
      setOrder('asc');
    }
  };

  const sortByDateActivity = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return dateCompare(a.lastAccessed, b.lastAccessed);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return dateCompare(a.lastAccessed, b.lastAccessed) * -1;
      });
      setOrder('asc');
    }
  };

  const statusCompare = (a, b) => {
    const statusA = a ? 0 : 1;
    const statusB = b ? 0 : 1;
    if (statusA < statusB) {
      return -1;
    }
    if (statusA > statusB) {
      return 1;
    }
    return 0;
  };

  const sortByAccountingStatus = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.accountingConnected, b.accountingConnected);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.accountingConnected, b.accountingConnected) * -1;
      });
      setOrder('asc');
    }
  };

  const sortByBankStatus = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.bankConnected, b.bankConnected);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.bankConnected, b.bankConnected) * -1;
      });
      setOrder('asc');
    }
  };

  const sortBySkvStatus = () => {
    if (order === 'asc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.skvConnected, b.skvConnected);
      });
      setOrder('desc');
    } else if (order === 'desc') {
      authLinks.sort((a, b) => {
        return statusCompare(a.skvConnected, b.skvConnected) * -1;
      });
      setOrder('asc');
    }
  };

  useEffect(() => {
    const getData = async () => {
      await AuthService.getAllExternalStates()
        .then((response) => {
          setAuthLinks(response.data);
        })
        .catch(() => {
          setAuthLinks([]);
        });
    };

    getData();
  }, []);

  return (
    <div className="settings-content">
      <div className="row-container">
        <XpektorButton
          content={gT(t, '.xpektor-btn.create-auth-link')}
          icon={faPlus}
          onClick={() => {
            setShowCreateAuthLinkModal(true);
          }}
        />
      </div>
      <AuthLinkTable
        sortByName={sortByName}
        sortByDateActivity={sortByDateActivity}
        sortByAccountingStatus={sortByAccountingStatus}
        sortByBankStatus={sortByBankStatus}
        sortBySkvStatus={sortBySkvStatus}
        sortByUserName={sortByUserName}
        sortByDateCreated={sortByDateCreated}
        order={order}
        t={t}
      >
        {authLinks.map((data) => (
          <AuthLinkTable.AuthLink
            authState={data}
            onClick={() => {
              setSelectedAuthLink(data);
              setShowAuthLinkModal(true);
            }}
            key={data.id}
            notAvailable={gsT(t, '.labels.not-available')}
          />
        ))}
      </AuthLinkTable>
      {showCreateAuthLinkModal && (
        <CreateClientModal
          handleClose={() => setShowCreateAuthLinkModal(false)}
          handleNewLink={() => fetchExternalStates()}
          t={t}
        />
      )}
      {showAuthLinkModal && (
        <AuthLinkModal
          authLink={selectedAuthLink}
          handleClose={() => setShowAuthLinkModal(false)}
          handleEdit={() => fetchExternalStates()}
          t={t}
        />
      )}
    </div>
  );
}
